import { useState, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import toast from 'react-hot-toast';
import { MOLE_SPRITES } from '../../../data/mole-sprites';

interface ChallengePanelProps {
  sessionId: string;
  socket: Socket | null;
  isConnected: boolean;
}

type ChallengeVariant = 'mole' | 'balance' | 'pixelart' | 'explore';

interface PatternInfo {
  id: string;
  name: string;
  grid: number[][];
  maxColors: number;
}
type PanelState = 'idle' | 'active' | 'result';


interface CompleteData {
  challengeId: string;
  result: 'success' | 'failure';
  variant: ChallengeVariant;
  finalScore: number;
  targetScore: number;
  whiteClicks?: number;
  blackClicks?: number;
  averageAccuracy?: number;
  collectiveAccuracy?: number;
}

const MINI_PALETTE = [
  '#374151', '#ef4444', '#3b82f6', '#22c55e', '#eab308',
  '#a855f7', '#f97316', '#ec4899', '#06b6d4',
];

export function ChallengePanel({ sessionId, socket, isConnected }: ChallengePanelProps) {
  // Config
  const [variant, setVariant] = useState<ChallengeVariant>('mole');
  const [duration, setDuration] = useState(30);
  const [targetScore, setTargetScore] = useState(500);
  const [teleportInterval, setTeleportInterval] = useState(600);
  const [balanceMargin, setBalanceMargin] = useState(10);

  // Mole sprite
  const [moleSpriteId, setMoleSpriteId] = useState('default');

  // Pixelart config
  const [pixelartNumColors, setPixelartNumColors] = useState(4);
  const [pixelartPatternId, setPixelartPatternId] = useState<string>('');
  const [pixelartAccuracyThreshold, setPixelartAccuracyThreshold] = useState(80);
  const [patterns, setPatterns] = useState<PatternInfo[]>([]);
  const [averageAccuracy, setAverageAccuracy] = useState(0);
  const [collectiveAccuracy, setCollectiveAccuracy] = useState(0);

  // State machine
  const [panelState, setPanelState] = useState<PanelState>('idle');
  const [activeChallengeId, setActiveChallengeId] = useState<string | null>(null);

  // Live data
  const [score, setScore] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [progressPercent, setProgressPercent] = useState(0);
  const [whiteClicks, setWhiteClicks] = useState(0);
  const [blackClicks, setBlackClicks] = useState(0);

  // Result data
  const [result, setResult] = useState<'success' | 'failure' | null>(null);
  const [finalScore, setFinalScore] = useState(0);

  // Fetch patterns on mount
  useEffect(() => {
    fetch('/api/challenges/patterns')
      .then(r => r.json())
      .then(data => {
        if (data.patterns) {
          setPatterns(data.patterns);
          if (data.patterns.length > 0 && !pixelartPatternId) {
            setPixelartPatternId(data.patterns[0].id);
          }
        }
      })
      .catch(err => console.error('[ChallengePanel] Error fetching patterns:', err));
  }, []);

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    const handleTick = (data: any) => {
      if (data.challengeId === activeChallengeId) {
        if (data.score !== undefined) setScore(data.score);
        setRemainingSeconds(data.remainingSeconds);
        if (data.progressPercent !== undefined) setProgressPercent(data.progressPercent);
        if (data.whiteClicks !== undefined) setWhiteClicks(data.whiteClicks);
        if (data.blackClicks !== undefined) setBlackClicks(data.blackClicks);
        if (data.averageAccuracy !== undefined) setAverageAccuracy(data.averageAccuracy);
      }
    };

    const handleProgress = (data: any) => {
      if (data.challengeId === activeChallengeId) {
        if (data.score !== undefined) setScore(data.score);
        if (data.progressPercent !== undefined) setProgressPercent(data.progressPercent);
        if (data.whiteClicks !== undefined) setWhiteClicks(data.whiteClicks);
        if (data.blackClicks !== undefined) setBlackClicks(data.blackClicks);
        if (data.averageAccuracy !== undefined) setAverageAccuracy(data.averageAccuracy);
      }
    };

    const handleComplete = (data: CompleteData) => {
      if (data.challengeId === activeChallengeId) {
        setPanelState('result');
        setResult(data.result);
        setFinalScore(data.finalScore);
        if (data.whiteClicks !== undefined) setWhiteClicks(data.whiteClicks);
        if (data.blackClicks !== undefined) setBlackClicks(data.blackClicks);
        if (data.averageAccuracy !== undefined) setAverageAccuracy(data.averageAccuracy);
        if (data.collectiveAccuracy !== undefined) setCollectiveAccuracy(data.collectiveAccuracy);
      }
    };

    const handleReset = () => {
      resetLocalState();
    };

    socket.on('challenge:tick', handleTick);
    socket.on('challenge:progress', handleProgress);
    socket.on('challenge:complete', handleComplete);
    socket.on('challenge:reset', handleReset);

    return () => {
      socket.off('challenge:tick', handleTick);
      socket.off('challenge:progress', handleProgress);
      socket.off('challenge:complete', handleComplete);
      socket.off('challenge:reset', handleReset);
    };
  }, [socket, activeChallengeId]);

  const resetLocalState = () => {
    setPanelState('idle');
    setActiveChallengeId(null);
    setScore(0);
    setRemainingSeconds(0);
    setProgressPercent(0);
    setWhiteClicks(0);
    setBlackClicks(0);
    setResult(null);
    setFinalScore(0);
    setAverageAccuracy(0);
    setCollectiveAccuracy(0);
  };

  const handleStart = async () => {
    if (!isConnected) {
      toast.error('Socket non connesso');
      return;
    }

    try {
      const body: any = {
        sessionId,
        variant,
        durationSeconds: variant === 'explore' ? 99999 : duration,
        targetScore,
      };

      if (variant === 'mole') {
        body.teleportIntervalMs = teleportInterval;
        body.moleSprite = moleSpriteId;
      } else if (variant === 'balance') {
        body.balanceMargin = balanceMargin;
      } else if (variant === 'pixelart') {
        body.pixelartNumColors = pixelartNumColors;
        body.pixelartTargetImageId = pixelartPatternId;
        body.pixelartAccuracyThreshold = pixelartAccuracyThreshold;
      }

      const response = await fetch('/api/challenges/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore avvio sfida');
      }

      const { challenge } = await response.json();
      setActiveChallengeId(challenge.id);
      setPanelState('active');
      setRemainingSeconds(duration);
      setScore(0);
      setProgressPercent(0);
      setWhiteClicks(0);
      setBlackClicks(0);

      toast.success('Sfida avviata!');
    } catch (error: any) {
      console.error('[ChallengePanel] Error starting:', error);
      toast.error(error.message || 'Errore avvio sfida');
    }
  };

  const handleStop = async () => {
    if (!activeChallengeId) return;

    try {
      await fetch(`/api/challenges/${activeChallengeId}/stop`, { method: 'POST' });
      toast.success('Sfida interrotta');
    } catch (error) {
      console.error('[ChallengePanel] Error stopping:', error);
      toast.error('Errore stop sfida');
    }
  };

  const handleReset = async () => {
    if (activeChallengeId) {
      try {
        await fetch(`/api/challenges/${activeChallengeId}/reset`, { method: 'POST' });
      } catch (error) {
        console.error('[ChallengePanel] Error resetting:', error);
      }
    }

    resetLocalState();

    if (socket && isConnected) {
      socket.emit('admin:set-projection', {
        sessionId,
        mode: { type: 'standby' },
      });
    }

    toast.success('Sfida resettata');
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <h3 className="text-xl font-bold text-white mb-4">Sfide</h3>

      {panelState === 'idle' && (
        <>
          {/* Selettore variante */}
          <div className="mb-6">
            <label className="text-gray-400 text-sm font-semibold block mb-2">
              Tipo di sfida
            </label>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => { setVariant('explore'); setTargetScore(500); setDuration(99999); }}
                className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                  variant === 'explore'
                    ? 'bg-teal-600 text-white ring-2 ring-teal-400'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
              >
                Esplora
              </button>
              <button
                onClick={() => { setVariant('mole'); setTargetScore(500); setDuration(30); }}
                className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                  variant === 'mole'
                    ? 'bg-red-600 text-white ring-2 ring-red-400'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
              >
                Prendi la talpa
              </button>
              <button
                onClick={() => { setVariant('balance'); setTargetScore(500); setDuration(30); }}
                className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                  variant === 'balance'
                    ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
              >
                Bilancia
              </button>
              <button
                onClick={() => { setVariant('pixelart'); setDuration(60); }}
                className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                  variant === 'pixelart'
                    ? 'bg-orange-600 text-white ring-2 ring-orange-400'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
              >
                Pixel Art
              </button>
            </div>
          </div>

          {/* Parametri */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {variant !== 'explore' && (
            <div>
              <label className="text-gray-400 text-sm font-semibold block mb-1">
                Durata (secondi)
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={duration}
                onChange={(e) => setDuration(e.target.value as any)}
                onBlur={(e) => { const n = parseInt(e.target.value); setDuration(isNaN(n) ? 30 : Math.max(10, Math.min(120, n))); }}
                className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            )}
            {variant !== 'pixelart' && (
              <div>
                <label className="text-gray-400 text-sm font-semibold block mb-1">
                  Obiettivo (punti)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={targetScore}
                  onChange={(e) => setTargetScore(e.target.value as any)}
                  onBlur={(e) => { const n = parseInt(e.target.value); setTargetScore(isNaN(n) ? 500 : Math.max(10, Math.min(10000, n))); }}
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            )}

            {variant === 'mole' && (
              <div>
                <label className="text-gray-400 text-sm font-semibold block mb-1">
                  Intervallo teleport (ms)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={teleportInterval}
                  onChange={(e) => setTeleportInterval(e.target.value as any)}
                  onBlur={(e) => { const n = parseInt(e.target.value); setTeleportInterval(isNaN(n) ? 600 : Math.max(500, Math.min(3000, n))); }}
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            )}

            {variant === 'balance' && (
              <div>
                <label className="text-gray-400 text-sm font-semibold block mb-1">
                  Margine equilibrio
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={balanceMargin}
                  onChange={(e) => setBalanceMargin(e.target.value as any)}
                  onBlur={(e) => { const n = parseInt(e.target.value); setBalanceMargin(isNaN(n) ? 10 : Math.max(1, Math.min(200, n))); }}
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            )}

            {variant === 'pixelart' && (
              <div>
                <label className="text-gray-400 text-sm font-semibold block mb-1">
                  Numero colori ({pixelartNumColors})
                </label>
                <input
                  type="range"
                  value={pixelartNumColors}
                  onChange={(e) => setPixelartNumColors(parseInt(e.target.value))}
                  min={2}
                  max={8}
                  className="w-full accent-orange-500"
                />
              </div>
            )}

            {variant === 'pixelart' && (
              <div>
                <label className="text-gray-400 text-sm font-semibold block mb-1">
                  Soglia accuratezza ({pixelartAccuracyThreshold}%)
                </label>
                <input
                  type="range"
                  value={pixelartAccuracyThreshold}
                  onChange={(e) => setPixelartAccuracyThreshold(parseInt(e.target.value))}
                  min={50}
                  max={100}
                  step={5}
                  className="w-full accent-orange-500"
                />
              </div>
            )}
          </div>

          {/* Mole sprite selector */}
          {variant === 'mole' && (
            <div className="mb-6">
              <label className="text-gray-400 text-sm font-semibold block mb-2">
                Sprite talpa
              </label>
              <div className="grid grid-cols-3 gap-3">
                {MOLE_SPRITES.map(sprite => (
                  <button
                    key={sprite.id}
                    onClick={() => setMoleSpriteId(sprite.id)}
                    className={`p-2 rounded-lg transition-all flex flex-col items-center ${
                      moleSpriteId === sprite.id
                        ? 'bg-red-600/30 ring-2 ring-red-400'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                  >
                    <div className="mb-1">{sprite.render(50)}</div>
                    <div className="text-gray-300 text-xs text-center">{sprite.name}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Pixelart pattern selector */}
          {variant === 'pixelart' && (
            <div className="mb-6">
              <label className="text-gray-400 text-sm font-semibold block mb-2">
                Immagine target
              </label>
              <div className="grid grid-cols-4 gap-3">
                {patterns.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setPixelartPatternId(p.id)}
                    className={`p-2 rounded-lg transition-all ${
                      pixelartPatternId === p.id
                        ? 'bg-orange-600/30 ring-2 ring-orange-400'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                  >
                    <div
                      className="grid gap-[0.5px] bg-gray-800 mx-auto mb-1"
                      style={{
                        gridTemplateColumns: 'repeat(10, 1fr)',
                        width: 50,
                        height: 50,
                      }}
                    >
                      {p.grid.map((row, r) =>
                        row.map((cell, c) => (
                          <div
                            key={`${r}-${c}`}
                            style={{ backgroundColor: MINI_PALETTE[cell] || '#374151' }}
                          />
                        ))
                      )}
                    </div>
                    <div className="text-gray-300 text-xs text-center">{p.name}</div>
                  </button>
                ))}
              </div>
              {!pixelartPatternId && (
                <p className="text-red-400 text-xs mt-2">Seleziona un pattern</p>
              )}
            </div>
          )}

          {/* Descrizione */}
          <div className="mb-6 p-4 bg-gray-700/50 rounded-lg">
            <p className="text-gray-300 text-sm">
              <strong>Come funziona:</strong>
            </p>
            {variant === 'explore' ? (
              <ul className="text-gray-400 text-sm mt-2 space-y-1 list-disc list-inside">
                <li>L'audience clicca un pulsante "Esplora"</li>
                <li>90% delle volte: non trovano nulla</li>
                <li>10% delle volte: trovano un oggetto (+1 punto globale)</li>
                <li>Nessun timer: la sfida finisce al raggiungimento dell'obiettivo</li>
                <li>Interrompi manualmente per farla fallire</li>
              </ul>
            ) : variant === 'mole' ? (
              <ul className="text-gray-400 text-sm mt-2 space-y-1 list-disc list-inside">
                <li>L'audience vede un cerchio che appare sullo schermo</li>
                <li>Ogni tap sul cerchio aggiunge 1 punto globale</li>
                <li>Raggiungere l'obiettivo entro il tempo = successo</li>
                <li>Il display mostra la progress bar e il countdown</li>
              </ul>
            ) : variant === 'balance' ? (
              <ul className="text-gray-400 text-sm mt-2 space-y-1 list-disc list-inside">
                <li>L'audience vede due bottoni: bianco e nero</li>
                <li>Ogni click aggiunge 1 punto E peso sulla bilancia</li>
                <li>Obiettivo: riempire la barra E mantenere l'equilibrio</li>
                <li>Differenza bianco/nero deve restare entro il margine</li>
              </ul>
            ) : (
              <ul className="text-gray-400 text-sm mt-2 space-y-1 list-disc list-inside">
                <li>Il proiettore mostra l'immagine target</li>
                <li>L'audience colora una griglia 10x10 sul telefono</li>
                <li>Il voto di maggioranza per cella forma la griglia collettiva</li>
                <li>Obiettivo: raggiungere la soglia di accuratezza</li>
              </ul>
            )}
          </div>

          {/* Pulsante avvia */}
          <button
            onClick={handleStart}
            disabled={!isConnected || (variant === 'pixelart' && !pixelartPatternId)}
            className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg font-bold transition-all"
          >
            Avvia Sfida
          </button>
        </>
      )}

      {panelState === 'active' && (
        <div className="py-4">
          {/* Timer + Progress */}
          <div className="text-center mb-6">
            {variant !== 'explore' && (
              <div className="text-6xl mb-2 font-bold text-orange-400 tabular-nums">
                {remainingSeconds}
              </div>
            )}
            <p className="text-xl text-white font-bold">
              {variant === 'explore' ? 'Esplora' : variant === 'mole' ? 'Prendi la talpa' : variant === 'balance' ? 'Bilancia' : 'Pixel Art'} in corso
            </p>
          </div>

          {/* Score / Accuracy */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-400 mb-1">
              <span>Progresso</span>
              <span>
                {variant === 'pixelart'
                  ? `${Math.round(averageAccuracy)}% / ${pixelartAccuracyThreshold}%`
                  : `${score} / ${targetScore}`
                }
              </span>
            </div>
            <div className="w-full h-4 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-200 ${
                  variant === 'pixelart'
                    ? 'bg-gradient-to-r from-orange-500 to-amber-400'
                    : variant === 'balance'
                      ? 'bg-gradient-to-r from-purple-600 to-blue-500'
                      : variant === 'explore'
                        ? 'bg-gradient-to-r from-teal-500 to-cyan-400'
                        : 'bg-gradient-to-r from-yellow-500 to-green-500'
                }`}
                style={{ width: `${variant === 'pixelart' ? Math.min(100, Math.round(averageAccuracy)) : progressPercent}%` }}
              />
            </div>
          </div>

          {/* Balance specifico */}
          {variant === 'balance' && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-white">{whiteClicks}</div>
                <div className="text-gray-400 text-xs">Bianco</div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                <div className={`text-2xl font-bold ${
                  Math.abs(whiteClicks - blackClicks) <= balanceMargin ? 'text-green-400' : 'text-red-400'
                }`}>
                  {Math.abs(whiteClicks - blackClicks)}
                </div>
                <div className="text-gray-400 text-xs">Diff (max {balanceMargin})</div>
              </div>
              <div className="bg-gray-600/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-300">{blackClicks}</div>
                <div className="text-gray-400 text-xs">Nero</div>
              </div>
            </div>
          )}

          {/* Pulsante interrompi */}
          <button
            onClick={handleStop}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-2 px-6 rounded-lg font-semibold transition-all"
          >
            Interrompi
          </button>
        </div>
      )}

      {panelState === 'result' && (
        <div className="text-center py-4">
          {result === 'success' ? (
            <>
              <div className="text-6xl mb-4">&#127881;</div>
              <p className="text-2xl text-white font-bold mb-2">Prova Superata!</p>
              {variant === 'pixelart' ? (
                <p className="text-4xl text-green-400 font-bold mb-4">
                  {Math.round(collectiveAccuracy)}%
                </p>
              ) : (
                <p className="text-4xl text-green-400 font-bold mb-4">{finalScore} / {targetScore}</p>
              )}
            </>
          ) : (
            <>
              <div className="text-6xl mb-4">&#10060;</div>
              <p className="text-2xl text-white font-bold mb-2">Prova Fallita</p>
              {variant === 'pixelart' ? (
                <p className="text-4xl text-red-400 font-bold mb-4">
                  {Math.round(collectiveAccuracy)}%
                </p>
              ) : (
                <p className="text-4xl text-red-400 font-bold mb-4">{finalScore} / {targetScore}</p>
              )}
            </>
          )}

          {variant === 'balance' && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <span className="text-white font-bold text-xl">{whiteClicks}</span>
                <div className="text-gray-400 text-xs">Bianco</div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                <span className={`font-bold text-xl ${
                  Math.abs(whiteClicks - blackClicks) <= balanceMargin ? 'text-green-400' : 'text-red-400'
                }`}>
                  {Math.abs(whiteClicks - blackClicks)}
                </span>
                <div className="text-gray-400 text-xs">Differenza</div>
              </div>
              <div className="bg-gray-600/50 rounded-lg p-3 text-center">
                <span className="text-gray-300 font-bold text-xl">{blackClicks}</span>
                <div className="text-gray-400 text-xs">Nero</div>
              </div>
            </div>
          )}

          <button
            onClick={handleReset}
            className="bg-purple-600 hover:bg-purple-700 text-white py-3 px-8 rounded-lg font-bold transition-all"
          >
            Reset
          </button>
        </div>
      )}

      {!isConnected && panelState === 'idle' && (
        <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-600 rounded-lg">
          <p className="text-yellow-300 text-sm">
            Socket non connesso. La sfida non può essere avviata.
          </p>
        </div>
      )}
    </div>
  );
}
